import React, { useEffect, useState } from 'react';
import cn from 'clsx';
import orderBy from 'lodash/orderBy';
import { ReactComponent as LampIcon } from 'assets/icons/New/lamp-icon.svg';
import { callComplianceDataAction } from 'views/ProductDetail/utils';
import { AModal, AButton, ACheckbox } from 'components/Antd';
import T from 'i18n-react';
import {
  TestDataHeader,
  CertificateDataHeader,
  OtherDataHeader,
  TestDataRow,
  CertificateDataRow,
  OtherDataRow,
} from './TableElements';
import { ADD_CD_TAB_KEYS } from './constants';
import classes from './AddCDModal.module.scss';

const AddCDModal = ({ open, onClose, onSubmit, productId, existingObj }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(ADD_CD_TAB_KEYS.TEST);
  const [data, setData] = useState({
    [ADD_CD_TAB_KEYS.TEST]: [],
    [ADD_CD_TAB_KEYS.CERTIFICATE]: [],
    [ADD_CD_TAB_KEYS.OTHER]: [],
  });
  const [checkAll, setCheckAll] = useState({
    [ADD_CD_TAB_KEYS.TEST]: false,
    [ADD_CD_TAB_KEYS.CERTIFICATE]: false,
    [ADD_CD_TAB_KEYS.OTHER]: false,
  });
  const [checks, setChecks] = useState({
    [ADD_CD_TAB_KEYS.TEST]: {},
    [ADD_CD_TAB_KEYS.CERTIFICATE]: {},
    [ADD_CD_TAB_KEYS.OTHER]: {},
  });
  let hasChecked = false;
  let checkedCount = 0;
  Object.keys(checks).forEach((cKey) => {
    const tTabChecks = Object.keys(checks[cKey]).filter((key) => !!checks[cKey][key]);
    checkedCount = checkedCount + tTabChecks.length;
    if (tTabChecks.length > 0) {
      hasChecked = true;
    }
  });
  const listData = data[activeTab];
  const checkItems = Object.keys(checks[activeTab]).filter((key) => !!checks[activeTab][key]);
  const checkItemsLength = checkItems.length;
  const loadTestData = async () => {
    const res = await callComplianceDataAction({
      key: 'test-data',
      action: 'get',
      id: productId,
      data: {},
    });
    const tList = orderBy(
      (res?.data?.data || []).filter((obj) => obj.status !== 'archived'),
      [
        (item) => (item.countryList?.name || '').toLowerCase(),
        (item) => (item.testItem?.name || '').toLowerCase(),
        (item) => (item.citation?.name || '').toLowerCase(),
      ],
      ['asc', 'asc', 'asc']
    );
    setData((prev) => ({
      ...prev,
      [ADD_CD_TAB_KEYS.TEST]: tList,
    }));
  };
  const loadCertificateData = async () => {
    const res = await callComplianceDataAction({
      key: 'certificate-data',
      action: 'get',
      id: productId,
      data: {},
    });
    const tList = (res?.data?.data || []).filter((obj) => obj.status !== 'archived');
    setData((prev) => ({
      ...prev,
      [ADD_CD_TAB_KEYS.CERTIFICATE]: tList,
    }));
  };
  const loadOtherData = async () => {
    const res = await callComplianceDataAction({
      key: 'other-data',
      action: 'get',
      id: productId,
      data: {},
    });
    const tList = (res?.data?.data || []).filter((obj) => obj.status !== 'archived');
    setData((prev) => ({
      ...prev,
      [ADD_CD_TAB_KEYS.OTHER]: tList,
    }));
  };
  const reloadData = () => {
    loadTestData();
    loadCertificateData();
    loadOtherData();
  };

  useEffect(() => {
    const eTestData = existingObj?.sObj?.testDataMaps || [];
    const eCertificateData = existingObj?.sObj?.certificateDataMaps || [];
    const eOtherData = existingObj?.sObj?.otherDataMaps || [];
    const defaultTestChecks = {};
    eTestData.forEach((obj) => {
      defaultTestChecks[obj.complianceTestDataId] = true;
    });
    const defaultCertificateChecks = {};
    eCertificateData.forEach((obj) => {
      defaultCertificateChecks[obj.complianceCertificateDataId] = true;
    });
    const defaultOtherChecks = {};
    eOtherData.forEach((obj) => {
      defaultOtherChecks[obj.complianceOtherDataId] = true;
    });
    const newChecks = {
      [ADD_CD_TAB_KEYS.TEST]: { ...defaultTestChecks },
      [ADD_CD_TAB_KEYS.CERTIFICATE]: { ...defaultCertificateChecks },
      [ADD_CD_TAB_KEYS.OTHER]: { ...defaultOtherChecks },
    };
    setChecks(newChecks);
    reloadData();
  }, []);

  return (
    <>
      <AModal
        title="Attach Compliance Data"
        open={open}
        onCancel={onClose}
        closable
        width={1000}
        className={cn(classes.modalWrapper)}>
        <div className={classes.mainContent}>
          <div className={classes.searchWrapper}>
            <span>Selected {checkedCount || 0} data in below data types</span>
          </div>
          <div className={classes.tabsHeader}>
            <div>
              <span
                onClick={() => {
                  setActiveTab(ADD_CD_TAB_KEYS.TEST);
                }}
                className={cn({
                  [classes.active]: activeTab === ADD_CD_TAB_KEYS.TEST,
                })}>
                Test Data
              </span>
              <span
                onClick={() => {
                  setActiveTab(ADD_CD_TAB_KEYS.CERTIFICATE);
                }}
                className={cn({
                  [classes.active]: activeTab === ADD_CD_TAB_KEYS.CERTIFICATE,
                })}>
                Certificate Data
              </span>
              <span
                onClick={() => {
                  setActiveTab(ADD_CD_TAB_KEYS.OTHER);
                }}
                className={cn({
                  [classes.active]: activeTab === ADD_CD_TAB_KEYS.OTHER,
                })}>
                Other
              </span>
            </div>
            <AButton type="primary" size="large" onClick={reloadData} htmlType="button">
              Reload Data
            </AButton>
          </div>
          <div className={classes.alert}>
            <LampIcon />
            <span>
              To add or edit data, please visit{' '}
              <span
                onClick={() => {
                  const win = window.open(
                    `/product/profile/${productId}?sections=compliance_data`,
                    '_blank'
                  );
                  if (win) win.focus();
                }}>
                Compliance Data
              </span>
              .
            </span>
          </div>
          <div
            className={cn(classes.listWrapper, {
              [classes.isTestData]: activeTab === ADD_CD_TAB_KEYS.TEST,
              [classes.isCertificateData]: activeTab === ADD_CD_TAB_KEYS.CERTIFICATE,
              [classes.isOtherData]: activeTab === ADD_CD_TAB_KEYS.OTHER,
            })}>
            <div className={classes.headerWrapper}>
              <div className={cn(classes.row, classes.header)}>
                <span>
                  <ACheckbox
                    checked={checkAll[activeTab]}
                    indeterminate={checkItemsLength > 0 && checkItemsLength < listData.length}
                    onChange={() => {
                      const newCheckAll = !checkAll[activeTab];
                      setCheckAll((prev) => ({
                        ...prev,
                        [activeTab]: newCheckAll,
                      }));
                      const newChecks = {};
                      listData.forEach((item) => {
                        newChecks[item.id] = newCheckAll;
                      });
                      setChecks((prev) => ({
                        ...prev,
                        [activeTab]: newChecks,
                      }));
                    }}
                  />
                </span>
                {activeTab === ADD_CD_TAB_KEYS.TEST && <TestDataHeader />}
                {activeTab === ADD_CD_TAB_KEYS.CERTIFICATE && <CertificateDataHeader />}
                {activeTab === ADD_CD_TAB_KEYS.OTHER && <OtherDataHeader />}
              </div>
            </div>
            <div className={classes.listInner}>
              {listData.map((item) => {
                return (
                  <div className={cn(classes.row, classes.content)} key={item.id}>
                    <span>
                      <ACheckbox
                        checked={!!checks[activeTab]?.[item.id]}
                        onChange={(e) => {
                          setChecks((prev) => ({
                            ...prev,
                            [activeTab]: {
                              ...prev[activeTab],
                              [item.id]: e.target.checked,
                            },
                          }));
                        }}
                      />
                    </span>
                    {activeTab === ADD_CD_TAB_KEYS.TEST && <TestDataRow data={item} />}
                    {activeTab === ADD_CD_TAB_KEYS.CERTIFICATE && (
                      <CertificateDataRow data={item} />
                    )}
                    {activeTab === ADD_CD_TAB_KEYS.OTHER && <OtherDataRow data={item} />}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.footer}>
            <AButton
              htmlType="button"
              type="primary"
              size="large"
              className={classes.confirmBtn}
              disabled={!hasChecked}
              loading={loading}
              onClick={() => {
                setLoading(true);
                onSubmit(checks, () => {
                  setLoading(false);
                });
              }}>
              Confirm
            </AButton>
          </div>
        </div>
      </AModal>
    </>
  );
};

export default AddCDModal;
